import React, { useState, useEffect, useContext, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { askPdfOrXlsx } from '../utils/pdfPrint';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3 } from 'lucide-react';
import { BalanceDetailsDialog, TransferEntriesDialog } from './financialTraceabilityDialogs';
import CashInflowsAuditDialog from './CashInflowsAuditDialog';
import NewCashTransferDialog from './NewCashTransferDialog';

const FinancialManagement = (() => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [activeTab, setActiveTab] = useState('balance'); // balance, transfer, expenses, cashbox, reports

  // 🆕 Cash Transfer v2 — new redesigned flow (Jul 2026)
  const [ctV2DialogOpen, setCtV2DialogOpen] = useState(false);

  // 🆕 Honor the navigation intent from the Dashboard "pending balance"
  // CTA. Legacy behaviour was to open the old Transfer tab; that tab is
  // now hidden so we auto-open the new v2 dialog instead.
  useEffect(() => {
    if (sessionStorage.getItem('pending-intent') === 'pending_balance') {
      sessionStorage.removeItem('pending-intent');
      setCtV2DialogOpen(true);
    }
  }, []);
  
  // Transfer states
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferRecipient, setTransferRecipient] = useState(''); // اسم المُستَلِم (سائق/مدير/محاسب…)
  const [transfers, setTransfers] = useState([]);
  const [allTransfers, setAllTransfers] = useState([]); // Unfiltered data

  // NEW (traceability layer): Balance details + transfer entries dialogs
  const [balanceDetailsOpen, setBalanceDetailsOpen] = useState(false);
  // 🆕 Standalone comprehensive cash-inflows audit dialog
  const [cashInflowsOpen, setCashInflowsOpen] = useState(false);
  const [transferEntriesOpen, setTransferEntriesOpen] = useState(false);
  // 🆕 In-page confirmation modal for rejecting a cash transfer. The
  // browser's confirm() was being auto-dismissed in some embedded
  // contexts (Emergent preview iframe), making the reject button feel
  // broken to the user. A controlled modal removes any ambiguity.
  const [rejectModal, setRejectModal] = useState({ open: false, transferId: null, busy: false });
  const [transferEntriesId, setTransferEntriesId] = useState(null);
  
  // Expense states
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('operational');
  const [expenses, setExpenses] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]); // Unfiltered data
  
  // Filter states (preserved across tabs)
  
  // NEW: Cashbox states
  const [cashboxBalance, setCashboxBalance] = useState(0);
  const [cashboxTransactions, setCashboxTransactions] = useState([]);
  const [cashboxLoading, setCashboxLoading] = useState(false);
  
  // Cashbox form states  
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDescription, setDepositDescription] = useState('');
  const [expenseCashAmount, setExpenseCashAmount] = useState('');
  const [expenseCashDescription, setExpenseCashDescription] = useState('');
  const [expenseCashType, setExpenseCashType] = useState('expense_hotel');
  // 🆕 Proof document attachments
  const [depositDocFile, setDepositDocFile] = useState(null);
  const [expenseDocFile, setExpenseDocFile] = useState(null);
  
  // NEW: General Expenses states
  const [generalExpenses, setGeneralExpenses] = useState([]);
  const [generalExpenseLoading, setGeneralExpenseLoading] = useState(false);
  
  // General expense form states (for manual entry)
  const [manualExpenseAmount, setManualExpenseAmount] = useState('');
  const [manualExpenseDescription, setManualExpenseDescription] = useState('');
  const [manualExpenseType, setManualExpenseType] = useState('office');
  const [manualExpenseVendor, setManualExpenseVendor] = useState('');
  const [manualExpenseReceipt, setManualExpenseReceipt] = useState('');
  
  // Filter states (preserved across tabs)
  const [filters, setFilters] = useState({
    selectedAgency: 'all', // 'all' or specific agency_id
    startDate: '', // Empty by default
    endDate: '', // Empty by default
    transferStatus: 'all', // 'all', 'pending', 'confirmed', 'rejected'
    expenseCategory: 'all' // 'all', 'operational', 'travel', 'supplies', 'other'
  });
  
  // Agencies list for filter
  const [agencies, setAgencies] = useState([]);
  
  // NEW: Installment integration states
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [installmentLoading, setInstallmentLoading] = useState(false);
  
  // NEW: Foreign currency states
  const [treasuryBalance, setTreasuryBalance] = useState(null);
  const [foreignPayments, setForeignPayments] = useState([]);
  const [foreignLoading, setForeignLoading] = useState(false);
  
  // Foreign payment form states
  const [foreignForm, setForeignForm] = useState({
    montant_dzd: '',
    taux_conversion: '',
    devise: 'EUR',
    agent_name: '',
    partenaire: '',
    reservation_name: '',
    date_operation: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Return form states
  const [returnForm, setReturnForm] = useState({
    reste_agent: '',
    date_return: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [selectedPaymentForReturn, setSelectedPaymentForReturn] = useState(null);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  
  // NEW: Image management states
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageDialogType, setImageDialogType] = useState(''); // 'transfer' or 'expense'
  // === Guided confirmation flow ===
  // When the general accountant / super admin clicks "تأكيد" on a transfer,
  // we open this modal first to capture the signed paper receipt as a photo
  // so the digital archive stays 100% linked to the reference number.
  const [confirmFlowTransfer, setConfirmFlowTransfer] = useState(null);
  const [confirmFlowFile, setConfirmFlowFile] = useState(null);
  const [confirmFlowBusy, setConfirmFlowBusy] = useState(false);
  const [selectedItemForImage, setSelectedItemForImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Reports
  const [dailyReport, setDailyReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  // 🆕 Ledger (date-range) report state
  const [ledger, setLedger] = useState(null);
  const [ledgerStart, setLedgerStart] = useState(new Date().toISOString().split('T')[0]);
  const [ledgerEnd, setLedgerEnd] = useState(new Date().toISOString().split('T')[0]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState('all'); // all | revenue | expense | transfer

  // ============================================================
  // 🛡️ Race-condition guard for the balance fetch.
  // The Financial page issues a balance request whenever:
  //   • the user changes the agency filter (very fast clicks)
  //   • a transfer / expense / cashbox tx is saved (multiple fetches in row)
  //   • the 30s auto-refresh timer fires
  // Without this guard, an OLDER /all response can land AFTER a NEWER
  // /agencies/{id}/balance response, painting the wrong total over the
  // selected agency's balance (the "239,480 instead of 180,000" symptom).
  // We track an ever-increasing request id and drop any response whose
  // id is not the latest, AND we cancel the in-flight axios call.
  // ============================================================
  const balanceReqIdRef = useRef(0);
  const balanceAbortRef = useRef(null);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  // CRITICAL: keep `transfers` and `expenses` in sync with the freshly-fetched
  // `allTransfers`/`allExpenses`. Previously a `setTimeout(applyFilters, 100)`
  // call re-synced them via a stale closure, which caused newly created
  // transfers/expenses to NOT appear in "آخر الحركات" until the next manual
  // refresh — staff thought their entry didn't save.
  useEffect(() => {
    setTransfers(allTransfers);
  }, [allTransfers]);

  useEffect(() => {
    setExpenses(allExpenses);
  }, [allExpenses]);

  // 🛠️  Bug fix (May 2026): the 30-second auto-refresh used to call
  // `fetchFinancialData` from a stale closure — captured at mount time
  // with `filters.selectedAgency === 'all'`. Result: a few seconds after
  // the user picked a specific agency, the silent background refresh
  // would overwrite the balance/totals with the "all agencies" numbers
  // (very annoying while working). We now keep a ref to the LATEST
  // fetcher so each interval tick uses the current filters.
  const fetchFinancialDataRef = useRef(null);

  // Auto-refresh every 30s so transfers/expenses created by other staff
  // become visible to managers/accountants without a manual page refresh.
  useEffect(() => {
    const interval = setInterval(() => {
      const dialogOpen = document.querySelector('[role="dialog"][data-state="open"]');
      if (dialogOpen) return; // pause while user is editing
      // Always call the LATEST fetchFinancialData (which closes over the
      // up-to-date filters) — not the stale one captured at mount time.
      if (typeof fetchFinancialDataRef.current === 'function') {
        fetchFinancialDataRef.current();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch agencies for filter (only for managers/accountants)
  useEffect(() => {
    if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
      fetchAgencies();
    }
  }, [user]);

  // NEW: Fetch foreign exchange data
  const fetchForeignExchangeData = async () => {
    setForeignLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get treasury balance
      const balanceResponse = await axios.get(`${API}/treasury/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTreasuryBalance(balanceResponse.data);
      
      // Get foreign payments
      const paymentsResponse = await axios.get(`${API}/foreign-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForeignPayments(paymentsResponse.data);
      
    } catch (error) {
      console.error('Error fetching foreign exchange data:', error);
      alert('فشل في تحميل بيانات العملات الأجنبية');
    } finally {
      setForeignLoading(false);
    }
  };

  // Create foreign payment
  const createForeignPayment = async () => {
    if (!foreignForm.montant_dzd || !foreignForm.taux_conversion || !foreignForm.agent_name || !foreignForm.partenaire) {
      alert('يرجى إدخال جميع البيانات المطلوبة');
      return;
    }

    if (parseFloat(foreignForm.montant_dzd) <= 0 || parseFloat(foreignForm.taux_conversion) <= 0) {
      alert('يرجى إدخال قيم صحيحة');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/foreign-payments`, foreignForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تسجيل التحويل الخارجي بنجاح');
      setForeignForm({
        montant_dzd: '',
        taux_conversion: '',
        devise: 'EUR',
        agent_name: '',
        partenaire: '',
        reservation_name: '',
        date_operation: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchForeignExchangeData();
    } catch (error) {
      console.error('Error creating foreign payment:', error);
      alert('❌ فشل في التحويل: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Return foreign payment
  const returnForeignPayment = async () => {
    if (!returnForm.reste_agent || !selectedPaymentForReturn) {
      alert('يرجى إدخال المبلغ المرجع');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/foreign-payments/${selectedPaymentForReturn.id}/return`, returnForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تسجيل الإرجاع بنجاح');
      setShowReturnDialog(false);
      setSelectedPaymentForReturn(null);
      setReturnForm({
        reste_agent: '',
        date_return: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchForeignExchangeData();
    } catch (error) {
      console.error('Error returning foreign payment:', error);
      alert('❌ فشل في الإرجاع: ' + (error.response?.data?.detail || error.message));
    }
  };

  // NEW: Fetch installment plans with operations
  const fetchInstallmentPlans = async () => {
    setInstallmentLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('=== FETCHING INSTALLMENT PLANS ===');
      
      // Get all service sales with installment plans
      const serviceSalesResponse = await axios.get(`${API}/service-sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const serviceSales = Array.isArray(serviceSalesResponse.data) ? serviceSalesResponse.data : [];
      console.log(`Found ${serviceSales.length} service sales`);
      
      const allPlans = [];
      
      // For each service sale, check if it has an installment plan
      for (const sale of serviceSales) {
        try {
          const planResponse = await axios.get(`${API}/service-sales/${sale.id}/installment-plan`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (planResponse.data) {
            // Get installment payments for this plan
            const paymentsResponse = await axios.get(`${API}/installment-plans/${planResponse.data.id}/payments`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const planWithDetails = {
              ...planResponse.data,
              service_sale: sale,
              payments: Array.isArray(paymentsResponse.data) ? paymentsResponse.data : [],
              totals: {
                total_payments: Array.isArray(paymentsResponse.data) ? paymentsResponse.data.length : 0,
                paid_payments: Array.isArray(paymentsResponse.data) ? 
                  paymentsResponse.data.filter(p => (p.paid_amount || 0) > 0).length : 0,
                total_paid_amount: Array.isArray(paymentsResponse.data) ? 
                  paymentsResponse.data.reduce((sum, p) => sum + (p.paid_amount || 0), 0) : 0
              }
            };
            
            allPlans.push(planWithDetails);
          }
        } catch (error) {
          // No installment plan for this service sale, skip
          continue;
        }
      }
      
      console.log(`Final result: ${allPlans.length} installment plans found`);
      setInstallmentPlans(allPlans);
      
    } catch (error) {
      console.error('Detailed error fetching installment plans:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show specific error message
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert('فشل في تحميل خطط التقسيط: ' + errorMessage);
    } finally {
      setInstallmentLoading(false);
    }
  };

  // Create daily operation from installment payment
  const createOperationFromInstallment = async (paymentId, installmentNumber) => {
    try {
      const token = localStorage.getItem('token');
      
      // First mark the payment as paid (if not already)
      const paymentParams = new URLSearchParams({
        paid_amount: 'full',
        payment_method: 'cash',
        notes: `دفعة ${installmentNumber} تلقائية`
      });
      
      await axios.put(`${API}/installment-payments/${paymentId}/pay?${paymentParams}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then create daily operation
      await axios.post(`${API}/installment-payments/${paymentId}/create-daily-operation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ تم تسجيل الدفعة ${installmentNumber} في العمليات اليومية`);
      fetchInstallmentPlans();
      fetchFinancialData(); // Refresh balance
    } catch (error) {
      console.error('Error creating operation from installment:', error);
      alert('❌ فشل في تسجيل الدفعة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Sync all installments with daily operations
  const syncAllInstallments = async () => {
    if (!confirm('هل تريد تسجيل جميع دفعات التقسيط المدفوعة في العمليات اليومية؟')) {
      return;
    }

    setInstallmentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/installment-plans/sync-with-daily-operations`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ تم تسجيل ${response.data.daily_operations_created || 0} عملية من دفعات التقسيط`);
      fetchInstallmentPlans();
      fetchFinancialData();
    } catch (error) {
      console.error('Error syncing installments:', error);
      alert('❌ فشل في التزامن: ' + (error.response?.data?.detail || error.message));
    } finally {
      setInstallmentLoading(false);
    }
  };

  // NEW: Fetch cashbox data when accessing cashbox tab
  useEffect(() => {
    if (activeTab === 'cashbox') {
      fetchCashboxData();
    }
  }, [activeTab, user]);

  // NEW: Fetch installment data when accessing installments tab
  useEffect(() => {
    if (activeTab === 'installments-integration') {
      fetchInstallmentPlans();
    }
  }, [activeTab, user]);

  // NEW: Fetch foreign exchange data when accessing foreign exchange tab
  useEffect(() => {
    if (activeTab === 'foreign-exchange') {
      fetchForeignExchangeData();
    }
  }, [activeTab, user]);

  // Apply filters when filters change
  useEffect(() => {
    if (!loading) {
      fetchFinancialData(); // Re-fetch data when filters change
    }
  }, [filters]);

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const fetchFinancialData = async () => {
    // Race-condition guard: assign this call a unique id + abort the
    // previous in-flight balance request so only the LATEST response
    // gets to update state.
    if (balanceAbortRef.current) {
      try { balanceAbortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const controller = new AbortController();
    balanceAbortRef.current = controller;    const myReqId = ++balanceReqIdRef.current;
    const isStale = () => myReqId !== balanceReqIdRef.current;

    console.log('=== FETCHING FINANCIAL DATA ===', 'reqId=', myReqId);
    console.log('User role:', user?.role);
    console.log('User agency:', user?.agency_id);

    try {
      const agencyId = user.agency_id;
      const token = localStorage.getItem('token');
      const authHeaders = {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      };

      // Fetch balance - for managers/accountants, use the consolidated
      // /agencies-balances/all endpoint (single round-trip, no N+1).
      if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
        try {
          if (filters.selectedAgency === 'all') {
            const r = await axios.get(`${API}/agencies-balances/all`, authHeaders);
            if (isStale()) return; // a newer request has superseded us
            setBalance(r.data?.totals || { total_revenue: 0, total_transferred: 0, total_expenses: 0, current_balance: 0 });
          } else {
            const balanceRes = await axios.get(`${API}/agencies/${filters.selectedAgency}/balance`, authHeaders);
            if (isStale()) return;
            setBalance(balanceRes.data);
          }
        } catch (e) {
          if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
          console.error('Balance fetch failed, falling back to per-agency:', e.message);
          // Fallback: per-agency parallel
          try {
            const allAgencies = await axios.get(`${API}/agencies`, authHeaders);
            const balanceResults = await Promise.allSettled(
              (allAgencies.data || []).map(a => axios.get(`${API}/agencies/${a.id}/balance`, authHeaders))
            );
            if (isStale()) return;
            const totalBalance = { total_revenue: 0, total_transferred: 0, pending_transferred: 0, total_expenses: 0, current_balance: 0 };
            balanceResults.forEach(res => {
              if (res.status === 'fulfilled') {
                const b = res.value.data || {};
                totalBalance.total_revenue += b.total_revenue || 0;
                totalBalance.total_transferred += b.total_transferred || 0;
                totalBalance.pending_transferred += b.pending_transferred || 0;
                totalBalance.total_expenses += b.total_expenses || 0;
                totalBalance.current_balance += b.current_balance || 0;
              }
            });
            setBalance(totalBalance);
          } catch (e2) {
            if (axios.isCancel?.(e2) || e2.name === 'CanceledError' || e2.code === 'ERR_CANCELED') return;
            if (!isStale()) setBalance({ total_revenue: 0, total_transferred: 0, total_expenses: 0, current_balance: 0 });
          }
        }
      } else {
        // For staff - only their agency balance
        try {
          const balanceRes = await axios.get(`${API}/agencies/${agencyId}/balance`, authHeaders);
          if (isStale()) return;
          setBalance(balanceRes.data);
        } catch (e) {
          if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
          if (!isStale()) setBalance({ total_revenue: 0, total_transferred: 0, total_expenses: 0, current_balance: 0 });
        }
      }
      
      // Build query parameters for filtering
      const queryParams = new URLSearchParams();
      
      if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
        // Apply filters for managers/accountants
        if (filters.selectedAgency !== 'all') {
          queryParams.append('agency_id', filters.selectedAgency);
        }
        if (filters.startDate) {
          queryParams.append('start_date', filters.startDate);
        }
        if (filters.endDate) {
          queryParams.append('end_date', filters.endDate);
        }
        if (filters.transferStatus !== 'all') {
          queryParams.append('status', filters.transferStatus);
        }
      }
      
      const queryString = queryParams.toString();
      
      // Fetch transfers with filters
      try {
        const transfersRes = await axios.get(`${API}/cash-transfers${queryString ? '?' + queryString : ''}`, authHeaders);
        if (isStale()) return;
        setAllTransfers(transfersRes.data || []);
      } catch (e) {
        if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
        console.error('Transfers fetch failed:', e.message);
        if (!isStale()) setAllTransfers([]);
      }
      
      // Fetch all expenses (managers/accountants can see all agencies with filters) — IN PARALLEL
      if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
        try {
          const allAgenciesRes = await axios.get(`${API}/agencies`, authHeaders);
          const allAgencies = allAgenciesRes.data || [];

          // Build query params for expense filtering (shared)
          const buildExpenseQuery = () => {
            const expenseParams = new URLSearchParams();
            if (filters.startDate) expenseParams.append('start_date', filters.startDate);
            if (filters.endDate) expenseParams.append('end_date', filters.endDate);
            if (filters.expenseCategory !== 'all') expenseParams.append('category', filters.expenseCategory);
            const q = expenseParams.toString();
            return q ? '?' + q : '';
          };
          const expQ = buildExpenseQuery();

          const targets = allAgencies.filter(a =>
            filters.selectedAgency === 'all' || a.id === filters.selectedAgency
          );
          const expenseResults = await Promise.allSettled(
            targets.map(a => axios.get(`${API}/agencies/${a.id}/expenses${expQ}`, authHeaders))
          );
          if (isStale()) return;
          let allExpensesData = [];
          expenseResults.forEach((res, i) => {
            if (res.status === 'fulfilled') {
              const agency = targets[i];
              const tagged = (res.value.data || []).map(expense => ({
                ...expense,
                agency_name: agency.name,
                agency_city: agency.city,
              }));
              allExpensesData = allExpensesData.concat(tagged);
            } else {
              console.error(`Expenses fetch failed for ${targets[i]?.name}:`, res.reason?.message);
            }
          });
          console.log('Expenses fetched:', allExpensesData.length);
          setAllExpenses(allExpensesData);
        } catch (e) {
          if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
          console.error('Bulk expenses fetch failed:', e.message);
          if (!isStale()) setAllExpenses([]);
        }
      } else {
        try {
          const expensesRes = await axios.get(`${API}/agencies/${agencyId}/expenses`, authHeaders);
          if (isStale()) return;
          console.log('Agency expenses fetched:', expensesRes.data?.length || 0);
          setAllExpenses(expensesRes.data || []);
        } catch (e) {
          if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
          if (!isStale()) setAllExpenses([]);
        }
      }
      
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching financial data:', error);
      // Last-resort: ensure UI doesn't hang on the loading card
      if (!isStale()) setBalance(prev => prev || { total_revenue: 0, total_transferred: 0, total_expenses: 0, current_balance: 0 });
    } finally {
      // Only the latest request flips loading off — older cancelled ones bail.
      if (!isStale()) setLoading(false);
    }
    // Note: `transfers` and `expenses` are auto-synced from `allTransfers`/
    // `allExpenses` via useEffect hooks above. Do NOT call applyFilters via
    // setTimeout — it would re-set state from a stale closure and wipe out
    // freshly created items (the bug that made staff entries "disappear").
  };

  const applyFilters = () => {
    // Kept as a no-op for compatibility; sync now happens via useEffects.
    setTransfers(allTransfers);
    setExpenses(allExpenses);
  };

  // Keep the ref pointing at the LATEST fetchFinancialData so the 30s
  // auto-refresh interval always sees the current `filters` value.
  // (See note on `fetchFinancialDataRef` declaration above for the bug
  // it fixes.)
  useEffect(() => {
    fetchFinancialDataRef.current = fetchFinancialData;
  });

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      selectedAgency: 'all',
      startDate: '',
      endDate: '',
      transferStatus: 'all',
      expenseCategory: 'all'
    });
  };

  const handleTransfer = async () => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    if (parseFloat(transferAmount) > balance?.current_balance) {
      alert('المبلغ أكبر من الرصيد المتاح');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API}/agencies/${user.agency_id}/cash-transfer`, {
        amount: parseFloat(transferAmount),
        notes: transferNotes,
        recipient_name: transferRecipient || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const refNo = resp?.data?.reference_no ? `\nالرقم المرجعي: ${resp.data.reference_no}` : '';
      alert(`✅ تم إنشاء طلب تحويل الأموال بنجاح${refNo}`);
      setTransferAmount('');
      setTransferNotes('');
      setTransferRecipient('');
      
      // Add small delay to ensure data is saved on server
      setTimeout(() => {
        fetchFinancialData();
      }, 500);
    } catch (error) {
      console.error('Error creating transfer:', error);
      alert('❌ فشل في إنشاء التحويل: ' + (error.response?.data?.detail || error.message));
    }
  };

  /**
   * Opens the cash-transfer receipt PDF in a new tab so the operator can
   * hand-print it and attach to the cash being physically transferred.
   * The receipt includes a unique reference number, sender (employee),
   * recipient line, amount and date — closing the paper trail outside
   * the application.
   */
  const handlePrintTransferReceipt = (transfer) => {
    const token = localStorage.getItem('token') || '';
    const url = `${API}/cash-transfers/${transfer.id}/print?token=${encodeURIComponent(token)}&_=${Date.now()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleExpense = async () => {
    if (!expenseAmount || !expenseDescription) {
      alert('يرجى إدخال المبلغ والوصف');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agencies/${user.agency_id}/expenses`, {
        amount: parseFloat(expenseAmount),
        description: expenseDescription,
        category: expenseCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تسجيل المصروف بنجاح');
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseCategory('operational');
      
      // STRONGER FIX: Multiple refresh attempts for expenses
      console.log('Refreshing financial data after expense...');
      
      // Immediate refresh
      fetchFinancialData();
      
      // Second refresh after delay
      setTimeout(() => {
        console.log('Second refresh for expenses...');
        fetchFinancialData();
      }, 1000);
      
      // Third refresh for stubborn cases
      setTimeout(() => {
        console.log('Final refresh for expenses...');
        fetchFinancialData();
      }, 2000);
    } catch (error) {
      console.error('Error creating expense:', error);
      alert('❌ فشل في تسجيل المصروف: ' + (error.response?.data?.detail || error.message));
    }
  };

  const confirmTransfer = async (transferId) => {
    // Find the transfer in current state to check if a signed-receipt photo
    // is already attached. If not, open the guided modal that uploads the
    // photo BEFORE confirming. This guarantees every confirmed transfer has
    // a paper-trail image stored in GridFS, linked to the reference number.
    const t = (allTransfers || transfers).find((x) => x.id === transferId);
    if (t && !t.document_image_url) {
      setConfirmFlowTransfer(t);
      setConfirmFlowFile(null);
      return;
    }
    await _doConfirmTransfer(transferId);
  };

  const _doConfirmTransfer = async (transferId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cash-transfers/${transferId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ تم تأكيد التحويل بنجاح');
      setTimeout(() => { fetchFinancialData(); }, 500);
    } catch (error) {
      console.error('Error confirming transfer:', error);
      alert('❌ فشل في تأكيد التحويل: ' + (error.response?.data?.detail || error.message));
    }
  };

  /**
   * Guided flow: upload the signed paper receipt photo then auto-confirm.
   * Used when the manager has the recipient-signed receipt in hand and is
   * about to confirm the transfer in the system.
   */
  const submitSignedReceiptAndConfirm = async () => {
    if (!confirmFlowTransfer) return;
    if (!confirmFlowFile) {
      alert('⚠️ يرجى اختيار صورة الوصل الموقّع أو تجاوز الخطوة');
      return;
    }
    setConfirmFlowBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', confirmFlowFile);
      await axios.post(
        `${API}/cash-transfers/${confirmFlowTransfer.id}/upload-image`,
        fd,
        { headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        } },
      );
      await _doConfirmTransfer(confirmFlowTransfer.id);
      setConfirmFlowTransfer(null);
      setConfirmFlowFile(null);
    } catch (e) {
      console.error('signed receipt upload error', e);
      alert('❌ فشل رفع صورة الوصل: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setConfirmFlowBusy(false);
    }
  };

  // Public handler: just opens the controlled modal. The actual API
  // call happens in confirmRejectTransfer below once the user clicks
  // "نعم، ارفض" inside the modal.
  const rejectTransfer = (transferId) => {
    setRejectModal({ open: true, transferId, busy: false });
  };

  const confirmRejectTransfer = async () => {
    const transferId = rejectModal.transferId;
    if (!transferId) return;
    setRejectModal(m => ({ ...m, busy: true }));
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.put(
        `${API}/cash-transfers/${transferId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      console.log('[rejectTransfer] OK', resp.status, resp.data);
      setRejectModal({ open: false, transferId: null, busy: false });
      // Refresh the cards + the transfers list immediately.
      await fetchFinancialData();
      alert('✅ تم رفض التحويل بنجاح');
    } catch (error) {
      console.error('[rejectTransfer] failed:', error);
      setRejectModal(m => ({ ...m, busy: false }));
      const detail = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'خطأ غير معروف';
      const code = error?.response?.status;
      alert(`❌ فشل في رفض التحويل (HTTP ${code || '?'})\n\n${detail}`);
    }
  };

  // NEW: Expense approval functions
  const approveExpense = async (expenseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/expenses/${expenseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم الموافقة على المصروف بنجاح');
      
      // Add delay for data refresh
      setTimeout(() => {
        fetchFinancialData();
      }, 500);
    } catch (error) {
      console.error('Error approving expense:', error);
      alert('❌ فشل في الموافقة على المصروف: ' + (error.response?.data?.detail || error.message));
    }
  };

  const rejectExpense = async (expenseId) => {
    if (!confirm('هل أنت متأكد من رفض هذا المصروف؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/expenses/${expenseId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم رفض المصروف بنجاح');
      
      // Add delay for data refresh
      setTimeout(() => {
        fetchFinancialData();
      }, 500);
    } catch (error) {
      console.error('Error rejecting expense:', error);
      alert('❌ فشل في رفض المصروف: ' + (error.response?.data?.detail || error.message));
    }
  };

  // NEW: Image management functions
  const handleImageUpload = async () => {
    if (!selectedImage || !selectedItemForImage) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      const endpoint = imageDialogType === 'transfer' 
        ? `${API}/cash-transfers/${selectedItemForImage.id}/upload-image`
        : `${API}/expenses/${selectedItemForImage.id}/upload-image`;

      await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('✅ تم رفع صورة الوثيقة بنجاح');
      setShowImageDialog(false);
      setSelectedImage(null);
      setSelectedItemForImage(null);
      setImageDialogType('');
      fetchFinancialData();
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.response?.status === 400) {
        alert('❌ خطأ في الصورة: ' + (error.response?.data?.detail || error.message));
      } else if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لرفع صورة لهذا العنصر');
      } else {
        alert('❌ فشل في رفع الصورة: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageRemove = async (itemId, type) => {
    if (!window.confirm('هل أنت متأكد من حذف صورة الوثيقة؟')) return;

    try {
      const endpoint = type === 'transfer' 
        ? `${API}/cash-transfers/${itemId}/remove-image`
        : `${API}/expenses/${itemId}/remove-image`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert('✅ تم حذف صورة الوثيقة بنجاح');
      fetchFinancialData();
    } catch (error) {
      console.error('Error removing image:', error);
      if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لحذف صورة هذا العنصر');
      } else {
        alert('❌ فشل في حذف الصورة: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openImageDialog = (item, type) => {
    setSelectedItemForImage(item);
    setImageDialogType(type);
    setSelectedImage(null);
    setShowImageDialog(true);
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('يرجى اختيار ملف صورة');
        return;
      }
      
      // Validate file size (2MB)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
        return;
      }

      // Check file extension
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        alert('نوع الملف غير مدعوم. يرجى اختيار صورة JPG أو PNG');
        return;
      }

      setSelectedImage(file);
    }
  };

  const fetchDailyReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/daily-financial/${user.agency_id}?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyReport(response.data);
    } catch (error) {
      console.error('Error fetching daily report:', error);
      alert('فشل في تحميل التقرير اليومي');
    }
  };

  // 🆕 Fetch the date-range financial ledger (every money movement, like
  // a bank statement). Backed by /api/reports/financial-ledger/{agency_id}.
  const fetchLedger = async () => {
    if (!ledgerStart || !ledgerEnd) {
      alert('يرجى تحديد تاريخ البداية والنهاية');
      return;
    }
    setLedgerLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/reports/financial-ledger/${user.agency_id}?start_date=${ledgerStart}&end_date=${ledgerEnd}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLedger(response.data);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      alert('فشل في تحميل دفتر الحركات المالية: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLedgerLoading(false);
    }
  };

  // 🆕 Print the ledger as a branded PDF (or Excel) via the shared
  // FormatPickerDialog (no native confirm — Chrome blocks them).
  const handlePrintLedger = async () => {
    if (!ledger) {
      alert('يرجى إنشاء دفتر الحركات أولاً ثم اضغط الطباعة');
      return;
    }
    try {
      await askPdfOrXlsx(
        `${API}/reports/financial-ledger/${user.agency_id}/print`,
        {
          start_date: ledgerStart,
          end_date: ledgerEnd,
          type_filter: ledgerFilter,
        }
      );
    } catch (error) {
      console.error('Error printing ledger:', error);
      alert('فشل في طباعة الدفتر: ' + error.message);
    }
  };

  // NEW: Cashbox functions
  const fetchCashboxData = async () => {
    if (!user || (user.role !== 'general_accountant' && user.role !== 'super_admin')) {
      return;
    }

    setCashboxLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cashbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCashboxBalance(response.data.balance);
      setCashboxTransactions(response.data.transactions);
    } catch (error) {
      console.error('Error fetching cashbox data:', error);
      alert('فشل في تحميل بيانات الخزينة');
    } finally {
      setCashboxLoading(false);
    }
  };

  const handleCashboxDeposit = async () => {
    if (!depositAmount || !depositDescription) {
      alert('يرجى إدخال المبلغ والوصف');
      return;
    }

    if (parseFloat(depositAmount) <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/cashbox/deposit`, {
        source_type: 'manager_deposit',
        description: depositDescription,
        amount: parseFloat(depositAmount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 🆕 Upload proof document if attached
      const txId = res.data?.transaction_id;
      if (depositDocFile && txId) {
        const fd = new FormData();
        fd.append('document', depositDocFile);
        try {
          await axios.post(
            `${API}/cashbox/transactions/${txId}/upload-document`,
            fd,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
          );
        } catch (e) {
          alert('⚠️ سُجل الضخ لكن فشل رفع الوثيقة: ' + (e.response?.data?.detail || e.message));
        }
      }

      alert(`✅ تم تسجيل الضخ بنجاح${depositDocFile ? ' مع وثيقة الإثبات' : ''}`);
      setDepositAmount('');
      setDepositDescription('');
      setDepositDocFile(null);
      fetchCashboxData();
    } catch (error) {
      console.error('Error recording deposit:', error);
      alert('❌ فشل في تسجيل الضخ: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCashboxExpense = async () => {
    if (!expenseCashAmount || !expenseCashDescription) {
      alert('يرجى إدخال المبلغ والوصف');
      return;
    }

    if (parseFloat(expenseCashAmount) <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    // Check if balance is sufficient
    if (parseFloat(expenseCashAmount) > cashboxBalance) {
      alert(`المبلغ أكبر من الرصيد المتاح (${cashboxBalance.toLocaleString()} دج)`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/cashbox/expense`, {
        source_type: expenseCashType,
        description: expenseCashDescription,
        amount: parseFloat(expenseCashAmount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 🆕 Upload proof document if attached
      const txId = res.data?.transaction_id;
      if (expenseDocFile && txId) {
        const fd = new FormData();
        fd.append('document', expenseDocFile);
        try {
          await axios.post(
            `${API}/cashbox/transactions/${txId}/upload-document`,
            fd,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
          );
        } catch (e) {
          alert('⚠️ سُجل المصروف لكن فشل رفع الوثيقة: ' + (e.response?.data?.detail || e.message));
        }
      }

      alert(`✅ تم تسجيل المصروف بنجاح${expenseDocFile ? ' مع وثيقة الإثبات' : ''}`);
      setExpenseCashAmount('');
      setExpenseCashDescription('');
      setExpenseCashType('expense_hotel');
      setExpenseDocFile(null);
      fetchCashboxData();
    } catch (error) {
      console.error('Error recording expense:', error);
      alert('❌ فشل في تسجيل المصروف: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💰 الإدارة المالية</h1>
          <p className="text-sm text-gray-600 mt-1">رصيد الوكالة والحركات المالية</p>
        </div>
      </div>

      {/* Document Review Alerts for Managers/Accountants */}
      {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-4 rtl:space-x-reverse">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <span className="text-amber-600 text-lg">📋</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-amber-900 mb-2">🔍 مراجعة الوثائق المالية</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-amber-800">
                      {transfers.filter(t => !t.document_image_url).length}
                    </div>
                    <div className="text-xs text-amber-700">تحويلات بدون وثائق</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-amber-800">
                      {expenses.filter(e => !e.document_image_url).length}
                    </div>
                    <div className="text-xs text-amber-700">مصاريف بدون وثائق</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">
                      {transfers.filter(t => t.document_image_url).length}
                    </div>
                    <div className="text-xs text-green-700">تحويلات موثقة</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">
                      {expenses.filter(e => e.document_image_url).length}
                    </div>
                    <div className="text-xs text-green-700">مصاريف موثقة</div>
                  </div>
                </div>
                <p className="text-sm text-amber-800">
                  💡 يمكنك مراجعة الوثائق المرفوعة والتأكد من صحتها، بالإضافة لإضافة وثائق إضافية حسب الحاجة.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Advanced Filters for Managers/Accountants */}
      {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
        <Card className="bg-brand-50 border-brand-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              🔍 فلاتر البحث والتصفية
            </CardTitle>
            <CardDescription>
              تصفية البيانات حسب الوكالة والتاريخ والحالة
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Agency Filter */}
              <div>
                <Label className="text-sm font-medium">🏢 الوكالة:</Label>
                <Select 
                  value={filters.selectedAgency} 
                  onValueChange={(value) => updateFilter('selectedAgency', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="اختر الوكالة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌐 جميع الوكالات</SelectItem>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.is_subagent ? '🤝' : '🏢'} {agency.name} - {agency.city}
                        {agency.is_subagent && ' (وكيل مُناوِل)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date Filter */}
              <div>
                <Label className="text-sm font-medium">📅 من تاريخ:</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => updateFilter('startDate', e.target.value)}
                  className="w-full"
                />
              </div>

              {/* End Date Filter */}
              <div>
                <Label className="text-sm font-medium">📅 إلى تاريخ:</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => updateFilter('endDate', e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Transfer Status Filter */}
              <div>
                <Label className="text-sm font-medium">📊 حالة التحويل:</Label>
                <Select 
                  value={filters.transferStatus} 
                  onValueChange={(value) => updateFilter('transferStatus', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌐 جميع الحالات</SelectItem>
                    <SelectItem value="pending">⏳ في الانتظار</SelectItem>
                    <SelectItem value="confirmed">✅ مؤكد</SelectItem>
                    <SelectItem value="rejected">❌ مرفوض</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Expense Category Filter */}
              <div>
                <Label className="text-sm font-medium">🏷️ نوع المصروف:</Label>
                <Select 
                  value={filters.expenseCategory} 
                  onValueChange={(value) => updateFilter('expenseCategory', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌐 جميع الأنواع</SelectItem>
                    <SelectItem value="operational">تشغيلية</SelectItem>
                    <SelectItem value="travel">سفر</SelectItem>
                    <SelectItem value="supplies">مستلزمات</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-brand-200">
              <div className="text-sm text-brand-700">
                📊 النتائج: {transfers.length} تحويل، {expenses.length} مصروف
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearFilters}
                  className="text-brand-600 border-brand-300 hover:bg-brand-100"
                >
                  🔄 مسح الفلاتر
                </Button>
                <Button 
                  size="sm"
                  onClick={() => fetchFinancialData()}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  🔍 تحديث البيانات
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 🆕 Cash Transfer v2 — new redesigned flow entry point.
          Prominent green button under the balance card so any staff
          member can open the new dialog in one click. */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-4 flex flex-wrap items-center gap-3 shadow-sm" data-testid="ct-v2-entry-card">
        <div className="text-3xl">📤</div>
        <div className="flex-1 min-w-[260px]">
          <div className="font-bold text-emerald-900 text-lg">تحويل نقدي جديد إلى الإدارة العامة</div>
          <div className="text-sm text-emerald-800 mt-0.5">
            اختر الوصولات (داخل + خارج) بدقة — الصافي هو المبلغ الذي سيصل نقداً.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCtV2DialogOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-md shadow"
          data-testid="ct-v2-open-btn"
        >
          ➕ إنشاء تحويل جديد
        </button>
        {/* 🆕 Persistent history CTA — always visible so users can
            reach the v2 Inbox / history without depending on the
            "pending" banner (which only appears when there is locked
            cash awaiting confirmation). */}
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'cash-transfers-inbox' }));
          }}
          className="bg-white hover:bg-emerald-50 text-emerald-700 border-2 border-emerald-600 text-sm font-bold px-5 py-2.5 rounded-md shadow-sm"
          data-testid="ct-v2-history-btn"
        >
          📋 عرض سجل التحويلات
        </button>
      </div>

      {/* 🔔 Pending-transfer alert — surfaces locked-but-unconfirmed cash
          so the manager doesn't chase a phantom gap between «الرصيد
          الحالي» و «الوصولات القابلة للتحويل». Only shows when there's
          actually money waiting on the accountant's confirmation. */}
      {balance && Number(balance.pending_transferred || 0) > 0 && (
        <div
          className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex flex-wrap items-center gap-3 shadow-sm"
          data-testid="pending-transfer-alert"
        >
          <div className="text-3xl">🔒</div>
          <div className="flex-1 min-w-[260px]">
            <div className="font-bold text-amber-900 text-lg">
              يوجد {Number(balance.pending_transferred).toLocaleString()} دج محبوسة في تحويلات قيد التأكيد
            </div>
            <div className="text-sm text-amber-800 mt-0.5">
              هذا المبلغ مخصوم من «الرصيد الحالي» لكنه ليس مفقوداً — ينتظر تأكيد المحاسب العام.
              إن لم يتم التأكيد، رفض التحويل سيعيد المبلغ تلقائياً إلى الرصيد المتاح.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              // 🆕 The legacy "transfer" tab (which contained the
              // #transfers-history-section) was removed in Jul 2026.
              // Route the user to the new v2 Inbox page instead —
              // that's where every submitted / pending / confirmed /
              // rejected transfer now lives.
              window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'cash-transfers-inbox' }));
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold px-4 py-2 rounded-md"
            data-testid="pending-transfer-cta"
          >
            عرض سجل التحويلات ←
          </button>
        </div>
      )}

      {/* Current Balance Card */}
      {balance ? (
        <Card className="bg-gradient-to-r from-brand-500 to-brand-600 text-white">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center" data-testid="kpi-current-balance">
                <div className="text-2xl font-bold">{balance.current_balance?.toLocaleString()}</div>
                <div className="text-brand-100">الرصيد الحالي (دج)</div>
              </div>
              <div className="text-center" data-testid="kpi-total-revenue">
                <div className="text-xl font-semibold">{balance.total_revenue?.toLocaleString()}</div>
                <div className="text-brand-100">إجمالي الإيرادات</div>
              </div>
              {/* 🆕 Pending Balance — cash locked in a transfer awaiting
                  the accountant's confirmation. Reduces current_balance
                  but is NOT a permanent debit: a rejection returns it. */}
              <div className="text-center bg-amber-500/30 rounded-md py-1" data-testid="kpi-pending-transferred">
                <div className="text-xl font-semibold flex items-center justify-center gap-1">
                  🔒 {balance.pending_transferred?.toLocaleString() || 0}
                </div>
                <div className="text-brand-100">الرصيد المعلق</div>
              </div>
              <div className="text-center" data-testid="kpi-total-transferred">
                <div className="text-xl font-semibold">{balance.total_transferred?.toLocaleString()}</div>
                <div className="text-brand-100">المحول للإدارة العامة</div>
              </div>
              <div className="text-center" data-testid="kpi-total-expenses">
                <div className="text-xl font-semibold">{balance.total_expenses?.toLocaleString()}</div>
                <div className="text-brand-100">إجمالي المصاريف</div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-gradient-to-r from-gray-400 to-gray-500 text-white">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold">تعذّر تحميل البيانات المالية</div>
              <div className="text-gray-100 mt-1">يرجى التأكد من الاتصال أو الضغط على إعادة المحاولة</div>
              <Button
                size="sm"
                onClick={() => { setLoading(true); fetchFinancialData(); }}
                className="mt-3 bg-white text-gray-700 hover:bg-gray-100"
                data-testid="financial-retry-btn"
              >
                🔄 إعادة المحاولة
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs - Always show tabs regardless of balance */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'balance', label: '💰 الرصيد الحالي', icon: '💰' },
            // 🗑️ Legacy "transfer" tab hidden (Jul 2026) — the new v2
            // flow lives inside the ➕ زر "إنشاء تحويل جديد" green card
            // above and the dedicated "📥 التحويلات النقدية الواردة"
            // page in the sidebar. Uncomment to re-enable if a rollback
            // is ever needed.
            // { id: 'transfer', label: '📤 تحويل أموال', icon: '📤' },
            { id: 'expenses', label: '💸 المصاريف', icon: '💸' },
            ...(user?.role === 'super_admin' ? 
              [{ id: 'cashbox', label: '🏦 الخزينة', icon: '🏦' }] : []
            ),
            ...(user?.role === 'general_accountant' || user?.role === 'super_admin' ? 
              [{ id: 'foreign-exchange', label: '💱 العملات الأجنبية', icon: '💱' }] : []
            ),
            { id: 'installments-integration', label: '📅 تكامل التقسيط', icon: '📅' },
            { id: 'reports', label: '📊 التقرير اليومي', icon: '📊' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Balance Tab */}
      {activeTab === 'balance' && balance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>📈 ملخص مالي</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span>إجمالي الإيرادات:</span>
                <span className="font-semibold text-green-600">{balance.total_revenue?.toLocaleString()} دج</span>
              </div>
              <div className="flex justify-between">
                <span>المحول للإدارة:</span>
                <span className="font-semibold text-brand-600">-{balance.total_transferred?.toLocaleString()} دج</span>
              </div>
              <div className="flex justify-between">
                <span>المصاريف:</span>
                <span className="font-semibold text-red-600">-{balance.total_expenses?.toLocaleString()} دج</span>
              </div>
              <hr />
              <div className="flex justify-between text-lg font-bold">
                <span>الرصيد الحالي:</span>
                <span className={balance.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {balance.current_balance?.toLocaleString()} دج
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>🔄 آخر الحركات</CardTitle>
              <CardDescription className="text-xs text-gray-500">
                ⏱️ تحديث تلقائي كل 30 ثانية — الأحدث أوّلاً
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(() => {
                  // Merge transfers + expenses, sort by most recent timestamp
                  const items = [
                    ...transfers.map(t => ({
                      kind: 'transfer',
                      id: 't-' + t.id,
                      ts: new Date(t.created_at || t.transfer_date || 0).getTime(),
                      data: t,
                    })),
                    ...expenses.map(e => ({
                      kind: 'expense',
                      id: 'e-' + e.id,
                      ts: new Date(e.created_at || e.expense_date || 0).getTime(),
                      data: e,
                    })),
                  ].sort((a, b) => b.ts - a.ts).slice(0, 8);

                  if (items.length === 0) {
                    return (
                      <p className="text-center text-gray-500 py-4">
                        لا توجد حركات بعد
                      </p>
                    );
                  }

                  return items.map(it => {
                    if (it.kind === 'transfer') {
                      const transfer = it.data;
                      return (
                        <div key={it.id} className="flex justify-between items-center py-2 border-b" data-testid={`recent-transfer-${transfer.id}`}>
                          <div>
                            <p className="font-medium">📤 تحويل للإدارة العامة</p>
                            <p className="text-sm text-gray-500">{formatDateWithEnglishNumerals(transfer.transfer_date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-brand-600">-{transfer.amount?.toLocaleString()} دج</p>
                            <p className="text-xs text-gray-500">{transfer.status === 'confirmed' ? '✅ مؤكد' : '⏳ في الانتظار'}</p>
                          </div>
                        </div>
                      );
                    }
                    const expense = it.data;
                    return (
                      <div key={it.id} className="flex justify-between items-center py-2 border-b" data-testid={`recent-expense-${expense.id}`}>
                        <div>
                          <p className="font-medium">💸 {expense.description}</p>
                          <p className="text-sm text-gray-500">{formatDateWithEnglishNumerals(expense.expense_date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-red-600">-{expense.amount?.toLocaleString()} دج</p>
                          <p className="text-xs text-gray-500">{expense.category}</p>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transfer Tab */}
      {activeTab === 'transfer' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>📤 تحويل أموال للإدارة العامة</CardTitle>
              <CardDescription>
                تحويل جزء من أموال الوكالة إلى الإدارة العامة
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>المبلغ المراد تحويله *</Label>
                <Input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="المبلغ بالدينار الجزائري"
                />
                <p className="text-xs text-gray-500">
                  الرصيد المتاح: {balance?.current_balance?.toLocaleString()} دج
                </p>
              </div>

              <div>
                <Label>اسم المُستَلِم (سائق / مدير / محاسب) <span className="text-red-500">*</span></Label>
                <Input
                  type="text"
                  value={transferRecipient}
                  onChange={(e) => setTransferRecipient(e.target.value)}
                  placeholder="مثال: السيد محمد — سائق التوصيل"
                  data-testid="transfer-recipient-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  اسم الشخص الذي سيستلم المبلغ نقداً — يُطبع على الوصل
                </p>
              </div>

              <div>
                <Label>ملاحظات</Label>
                <Textarea
                  value={transferNotes}
                  onChange={(e) => setTransferNotes(e.target.value)}
                  placeholder="ملاحظات حول التحويل (اختياري)"
                />
              </div>

              <Button 
                onClick={handleTransfer} 
                className="w-full bg-brand-600 hover:bg-brand-700"
                disabled={!transferAmount || parseFloat(transferAmount) <= 0}
              >
                📤 إنشاء طلب التحويل
              </Button>

              {/* NEW — Detailed traceability path: pick the exact financial
                  entries that compose the transferable balance, then
                  create a cash-transfer that locks them. */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-amber-500 text-amber-700 hover:bg-amber-50"
                onClick={() => setBalanceDetailsOpen(true)}
                data-testid="open-balance-details"
              >
                🔍 تفاصيل الرصيد + تحويل مُتتبَّع
              </Button>
              <p className="text-[11px] text-gray-500 -mt-2">
                اعرض كل وصل مكوِّن للرصيد، اختر الوصولات المراد تحويلها وأنشئ
                تحويلًا مرتبطًا بها مع منع تعديلها لاحقًا.
              </p>
              {/* 🆕 Comprehensive cash inflows audit — shows EVERY cash-in
                  source (trip_payments, installment, flight, manual...) with
                  a date filter. Independent of the transfer flow. */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setCashInflowsOpen(true)}
                data-testid="open-cash-inflows-audit"
              >
                📊 كل التدفقات النقدية (تدقيق شامل)
              </Button>
              <p className="text-[11px] text-gray-500 -mt-2">
                رؤية شاملة لكل الأموال الداخلة للوكالة في فترة محددة —
                من عمليات، رحلات، أقساط، طيران، وغيرها.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="transfers-history-section">
            <CardHeader>
              <CardTitle>📋 سجل التحويلات</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {transfers.map((transfer) => (
                  <div key={transfer.id} className="p-3 border rounded-lg" data-testid={`transfer-row-${transfer.id}`}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-medium">{transfer.amount?.toLocaleString()} دج</span>
                        {/* 🆕 Deductions summary — refunds + discounts + other
                            expenses that hit the agency after this transfer
                            until the next one (or NOW for the most recent).
                            Shows the manager whether the transferred cash was
                            fully consumed, so the balance shown in this list
                            reflects reality. */}
                        {(transfer.deductions_total > 0) && (
                          <div className="mt-1 space-y-0.5" data-testid={`deductions-summary-${transfer.id}`}>
                            <div className="flex flex-wrap items-center gap-1 text-[10px]">
                              {transfer.deductions_refunds > 0 && (
                                <span className="px-1.5 py-0.5 bg-rose-50 text-rose-800 border border-rose-200 rounded" title="مجموع الاسترجاعات في الفترة">
                                  ↩️ -{Number(transfer.deductions_refunds).toLocaleString()}
                                </span>
                              )}
                              {transfer.deductions_discounts > 0 && (
                                <span className="px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded" title="مجموع سندات التخفيض في الفترة">
                                  💸 -{Number(transfer.deductions_discounts).toLocaleString()}
                                </span>
                              )}
                              {transfer.deductions_other > 0 && (
                                <span className="px-1.5 py-0.5 bg-orange-50 text-orange-800 border border-orange-200 rounded" title="مجموع المصاريف العامة في الفترة">
                                  📦 -{Number(transfer.deductions_other).toLocaleString()}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] font-mono">
                              <span className="text-gray-500">إجمالي الخصم:</span>
                              <span className="text-red-700 font-bold mx-1">
                                -{Number(transfer.deductions_total).toLocaleString()} دج
                              </span>
                              <span className="text-gray-500">| الصافي:</span>
                              <span className={`font-bold mx-1 ${(transfer.net_after_deductions ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                {Number(transfer.net_after_deductions ?? 0).toLocaleString()} دج
                              </span>
                            </div>
                          </div>
                        )}
                        {transfer.reference_no && (
                          <span className="text-[10px] font-mono text-gray-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded mt-0.5 w-fit">
                            🔖 {transfer.reference_no}
                          </span>
                        )}
                        {transfer.recipient_name && (
                          <span className="text-xs text-blue-700 mt-1">
                            👤 المُستَلِم: {transfer.recipient_name}
                          </span>
                        )}
                        {(user?.role === 'general_accountant' || user?.role === 'super_admin') && transfer.agency_name && (
                          <span className="text-xs text-brand-600 bg-brand-100 px-2 py-0.5 rounded-full mt-1 w-fit">
                            🏢 {transfer.agency_name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        {/* Document Image */}
                        <div className="flex items-center space-x-1">
                          {transfer.document_image_url ? (
                            <div className="flex items-center space-x-1">
                              <div className="relative group">
                                <img 
                                  src={`${API}${transfer.document_image_url}`}
                                  alt="وثيقة التحويل"
                                  className="w-8 h-8 object-cover rounded border cursor-pointer hover:scale-110 transition-transform"
                                  onClick={() => window.open(`${API}${transfer.document_image_url}`, '_blank')}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                                <div 
                                  className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-xs" 
                                  style={{display: 'none'}}
                                >
                                  ❌
                                </div>
                                {/* Image info tooltip for managers/accountants */}
                                {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    📋 للمراجعة - انقر للعرض
                                  </div>
                                )}
                              </div>
                              
                              {/* Manager/Accountant Controls */}
                              {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openImageDialog(transfer, 'transfer')}
                                    className="text-brand-600 hover:text-brand-700 p-1 h-6 w-6"
                                    title="إضافة وثيقة إضافية"
                                  >
                                    📷
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleImageRemove(transfer.id, 'transfer')}
                                    className="text-red-600 hover:text-red-700 p-1 h-6 w-6"
                                    title="حذف الوثيقة"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              )}
                              
                              {/* Employee view - no controls */}
                              {user?.role === 'agency_staff' && transfer.transferred_by === user.id && (
                                <span className="text-xs text-green-600 bg-green-100 px-1 py-0.5 rounded">
                                  ✅ مرفوعة
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openImageDialog(transfer, 'transfer')}
                                className="text-brand-600 hover:text-brand-700 text-xs"
                              >
                                {(user?.role === 'general_accountant' || user?.role === 'super_admin') ? 
                                  '📷 إدارة الوثيقة' : '📷 إضافة وثيقة'
                                }
                              </Button>
                              
                              {/* Missing document indicator for managers/accountants */}
                              {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                <span className="text-xs text-orange-600 bg-orange-100 px-1 py-0.5 rounded">
                                  ⚠️ مفقودة
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintTransferReceipt(transfer)}
                          className="text-amber-700 border-amber-400 hover:bg-amber-50 px-2 py-1 text-xs font-bold"
                          title="فتح وصل التحويل لطباعته وتسليمه مع المبلغ"
                          data-testid={`print-transfer-${transfer.id}`}
                        >
                          🖨️ وصل
                        </Button>

                        {/* NEW — View linked financial entries (traceability).
                            Visible on every row; the dialog itself shows a
                            warning banner when the transfer is legacy and
                            has no linked entries. */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setTransferEntriesId(transfer.id);
                            setTransferEntriesOpen(true);
                          }}
                          className="text-blue-700 border-blue-300 hover:bg-blue-50 px-2 py-1 text-xs font-bold"
                          title="عرض الوصولات المالية المرتبطة بهذا التحويل"
                          data-testid={`view-transfer-entries-${transfer.id}`}
                        >
                          🔍 تفاصيل
                          {Array.isArray(transfer.linked_entry_ids) && transfer.linked_entry_ids.length > 0 && (
                            <span className="ms-1 bg-blue-600 text-white rounded-full text-[10px] px-1.5 py-0.5">
                              {transfer.linked_entry_ids.length}
                            </span>
                          )}
                        </Button>

                        <span className={`px-2 py-1 rounded-full text-xs ${
                          transfer.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          transfer.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transfer.status === 'confirmed' ? '✅ مؤكد' :
                           transfer.status === 'pending' ? '⏳ في الانتظار' : 
                           transfer.status === 'rejected' ? '❌ مرفوض' : '❌ ملغي'}
                        </span>
                        
                        {/* Confirmation buttons for pending transfers (only for authorized users) */}
                        {transfer.status === 'pending' && (user.role === 'general_accountant' || user.role === 'super_admin') && (
                          <div className="flex space-x-1 rtl:space-x-reverse">
                            <Button
                              size="sm"
                              onClick={() => confirmTransfer(transfer.id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs"
                            >
                              ✅ تأكيد
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => rejectTransfer(transfer.id)}
                              className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 px-2 py-1 text-xs"
                              data-testid={`reject-transfer-btn-${transfer.id}`}
                            >
                              ❌ رفض
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>{formatDateWithEnglishNumerals(transfer.transfer_date)}</span>
                      {transfer.confirmation_by && (
                        <span className="text-xs">
                          {transfer.status === 'confirmed' ? 'أكد من قبل:' : 'رفض من قبل:'} {transfer.confirmation_by}
                        </span>
                      )}
                    </div>
                    {transfer.notes && <p className="text-sm text-gray-500 mt-1">{transfer.notes}</p>}
                  </div>
                ))}
                {transfers.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا توجد تحويلات مسجلة</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>💸 تسجيل مصروف جديد</CardTitle>
              <CardDescription>
                تسجيل المصاريف التي تنخصم من رصيد الوكالة
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>مبلغ المصروف *</Label>
                <Input
                  type="number"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  placeholder="المبلغ بالدينار الجزائري"
                />
              </div>

              <div>
                <Label>وصف المصروف *</Label>
                <Input
                  value={expenseDescription}
                  onChange={(e) => setExpenseDescription(e.target.value)}
                  placeholder="مثال: بنزين، إيجار، مصاريف تشغيلية"
                />
              </div>

              <div>
                <Label>نوع المصروف</Label>
                <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operational">تشغيلية</SelectItem>
                    <SelectItem value="travel">سفر</SelectItem>
                    <SelectItem value="supplies">مستلزمات</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleExpense} 
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={!expenseAmount || !expenseDescription}
              >
                💸 تسجيل المصروف
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>📋 سجل المصاريف</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {expenses.map((expense) => (
                  <div key={expense.id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-medium">{expense.description}</span>
                        {(user?.role === 'general_accountant' || user?.role === 'super_admin') && expense.agency_name && (
                          <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full mt-1 w-fit">
                            🏢 {expense.agency_name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Document Image */}
                        <div className="flex items-center space-x-1">
                          {expense.document_image_url ? (
                            <div className="flex items-center space-x-1">
                              <div className="relative group">
                                <img 
                                  src={`${API}${expense.document_image_url}`}
                                  alt="وثيقة المصروف"
                                  className="w-8 h-8 object-cover rounded border cursor-pointer hover:scale-110 transition-transform"
                                  onClick={() => window.open(`${API}${expense.document_image_url}`, '_blank')}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                                <div 
                                  className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-xs" 
                                  style={{display: 'none'}}
                                >
                                  ❌
                                </div>
                                {/* Image info tooltip for managers/accountants */}
                                {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    📋 للمراجعة - انقر للعرض
                                  </div>
                                )}
                              </div>
                              
                              {/* Manager/Accountant Controls */}
                              {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openImageDialog(expense, 'expense')}
                                    className="text-brand-600 hover:text-brand-700 p-1 h-6 w-6"
                                    title="إضافة وثيقة إضافية"
                                  >
                                    📷
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleImageRemove(expense.id, 'expense')}
                                    className="text-red-600 hover:text-red-700 p-1 h-6 w-6"
                                    title="حذف الوثيقة"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              )}
                              
                              {/* Employee view - no controls */}
                              {user?.role === 'agency_staff' && expense.employee_id === user.id && (
                                <span className="text-xs text-green-600 bg-green-100 px-1 py-0.5 rounded">
                                  ✅ مرفوعة
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openImageDialog(expense, 'expense')}
                                className="text-brand-600 hover:text-brand-700 text-xs"
                              >
                                {(user?.role === 'general_accountant' || user?.role === 'super_admin') ? 
                                  '📷 إدارة الوثيقة' : '📷 إضافة وثيقة'
                                }
                              </Button>
                              
                              {/* Missing document indicator for managers/accountants */}
                              {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                <span className="text-xs text-orange-600 bg-orange-100 px-1 py-0.5 rounded">
                                  ⚠️ مفقودة
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="font-bold text-red-600">{expense.amount?.toLocaleString()} دج</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span>{formatDateWithEnglishNumerals(expense.expense_date)}</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-gray-100 px-2 py-1 rounded">{expense.category}</span>
                        
                        {/* Expense Status */}
                        {expense.status === 'approved' && (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            ✅ مقبول
                          </span>
                        )}
                        {expense.status === 'rejected' && (
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                            ❌ مرفوض
                          </span>
                        )}
                        {(!expense.status || expense.status === 'pending') && (
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                            ⏳ في الانتظار
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Approval buttons for pending expenses (only for authorized users) */}
                    {(!expense.status || expense.status === 'pending') && 
                     (user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                      <div className="flex space-x-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => approveExpense(expense.id)}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs"
                        >
                          ✅ موافقة
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => rejectExpense(expense.id)}
                          className="text-red-600 hover:text-red-700 border-red-300 text-xs"
                        >
                          ❌ رفض
                        </Button>
                      </div>
                    )}
                    
                    {/* Show who approved/rejected */}
                    {(expense.status === 'approved' || expense.status === 'rejected') && expense.approved_by && (
                      <div className="text-xs text-gray-600 mt-1">
                        {expense.status === 'approved' ? 'وافق عليه:' : 'رفضه:'} {expense.approved_by}
                      </div>
                    )}
                  </div>
                ))}
                {expenses.length === 0 && (
                  <p className="text-center text-gray-500 py-4">لا توجد مصاريف مسجلة</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* NEW: Cashbox Tab (Only for General Accountant and Super Admin) */}
      {activeTab === 'cashbox' && (user?.role === 'general_accountant' || user?.role === 'super_admin') && (
        <div className="space-y-6">
          {/* Current Balance Card */}
          <Card className="bg-gradient-to-r from-purple-50 to-brand-50 border-purple-200">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                🏦 رصيد الخزينة الحالي
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cashboxLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-700 mb-2">
                    {cashboxBalance.toLocaleString()} دج
                  </div>
                  <div className="text-sm text-purple-600">الرصيد المتاح في الخزينة</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operations Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Deposit Card (Super Admin Only) */}
            {user?.role === 'super_admin' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    💰 ضخ من المدير
                  </CardTitle>
                  <CardDescription>
                    إضافة أموال إلى الخزينة من قبل الإدارة
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>المبلغ (دج)</Label>
                    <Input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="أدخل مبلغ الضخ"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>الوصف</Label>
                    <Input
                      value={depositDescription}
                      onChange={(e) => setDepositDescription(e.target.value)}
                      placeholder="وصف عملية الضخ"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">📎 وثيقة الإثبات (اختياري)</Label>
                    <Input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) { setDepositDocFile(null); return; }
                        const allowed = ['image/jpeg','image/jpg','image/png','application/pdf'];
                        if (!allowed.includes(f.type)) { alert('❌ JPG / PNG / PDF فقط'); e.target.value=''; setDepositDocFile(null); return; }
                        if (f.size > 4 * 1024 * 1024) { alert('❌ الحد الأقصى 4 ميغا'); e.target.value=''; setDepositDocFile(null); return; }
                        setDepositDocFile(f);
                      }}
                      data-testid="deposit-document"
                      className="cursor-pointer mt-1"
                    />
                    {depositDocFile && (
                      <div className="mt-1 text-xs bg-emerald-50 border border-emerald-200 rounded p-1.5 text-emerald-800">
                        ✅ {depositDocFile.name} ({(depositDocFile.size/1024).toFixed(0)} KB)
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={handleCashboxDeposit}
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={!depositAmount || !depositDescription}
                  >
                    💰 تسجيل الضخ
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Expense Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  💸 مصروف من الخزينة
                </CardTitle>
                <CardDescription>
                  تسجيل مصروف (فندق، نقل، أو أخرى)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>نوع المصروف</Label>
                  <select
                    value={expenseCashType}
                    onChange={(e) => setExpenseCashType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="expense_hotel">🏨 مصروف فندق</option>
                    <option value="expense_transport">🚌 مصروف نقل</option>
                    <option value="expense_other">📋 مصروف آخر</option>
                  </select>
                </div>
                <div>
                  <Label>المبلغ (دج)</Label>
                  <Input
                    type="number"
                    value={expenseCashAmount}
                    onChange={(e) => setExpenseCashAmount(e.target.value)}
                    placeholder="مبلغ المصروف"
                    min="0"
                    max={cashboxBalance}
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    الرصيد المتاح: {cashboxBalance.toLocaleString()} دج
                  </p>
                </div>
                <div>
                  <Label>الوصف</Label>
                  <Input
                    value={expenseCashDescription}
                    onChange={(e) => setExpenseCashDescription(e.target.value)}
                    placeholder="وصف المصروف"
                  />
                </div>
                <div>
                  <Label className="text-xs">📎 وثيقة الإثبات (اختياري)</Label>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) { setExpenseDocFile(null); return; }
                      const allowed = ['image/jpeg','image/jpg','image/png','application/pdf'];
                      if (!allowed.includes(f.type)) { alert('❌ JPG / PNG / PDF فقط'); e.target.value=''; setExpenseDocFile(null); return; }
                      if (f.size > 4 * 1024 * 1024) { alert('❌ الحد الأقصى 4 ميغا'); e.target.value=''; setExpenseDocFile(null); return; }
                      setExpenseDocFile(f);
                    }}
                    data-testid="expense-document"
                    className="cursor-pointer mt-1"
                  />
                  {expenseDocFile && (
                    <div className="mt-1 text-xs bg-amber-50 border border-amber-200 rounded p-1.5 text-amber-800">
                      ✅ {expenseDocFile.name} ({(expenseDocFile.size/1024).toFixed(0)} KB)
                    </div>
                  )}
                </div>
                <Button 
                  onClick={handleCashboxExpense}
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={!expenseCashAmount || !expenseCashDescription || parseFloat(expenseCashAmount) > cashboxBalance}
                >
                  💸 تسجيل المصروف
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Transactions History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                📋 سجل العمليات الأخيرة
              </CardTitle>
              <CardDescription>
                آخر 50 عملية في الخزينة
              </CardDescription>
            </CardHeader>
            <CardContent>
              {cashboxLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {cashboxTransactions.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد عمليات مسجلة</p>
                  ) : (
                    cashboxTransactions.map((transaction) => (
                      <div key={transaction.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex flex-col">
                            <span className="font-medium">{transaction.description}</span>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                                {transaction.source_type === 'manager_deposit' && '💰 ضخ من المدير'}
                                {transaction.source_type === 'agency_transfer' && '📥 تحويل من وكالة'}
                                {transaction.source_type === 'expense_hotel' && '🏨 مصروف فندق'}
                                {transaction.source_type === 'expense_transport' && '🚌 مصروف نقل'}
                                {transaction.source_type === 'expense_other' && '📋 مصروف آخر'}
                              </span>
                              {transaction.agency_name && (
                                <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                  🏢 {transaction.agency_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${
                              transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()} دج
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-sm text-gray-500">
                          <span>
                            {new Date(transaction.created_at).toLocaleDateString('ar-DZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          <div className="flex items-center gap-2">
                            {transaction.document_image_url && (
                              <a
                                href={`${API}${transaction.document_image_url}?token=${localStorage.getItem('token')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-700 hover:text-emerald-900 underline text-xs"
                                title="عرض وثيقة الإثبات"
                                data-testid={`tx-doc-${transaction.id}`}
                              >📎 وثيقة</a>
                            )}
                            <span>{transaction.created_by_name}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* NEW: Installments Integration Tab */}
      {activeTab === 'installments-integration' && (
        <div className="space-y-6">
          {/* Sync Button for Admins */}
          {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
            <Card className="bg-brand-50 border-brand-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  🔄 تزامن خطط التقسيط مع العمليات اليومية
                </CardTitle>
                <CardDescription>
                  تسجيل جميع دفعات التقسيط المدفوعة كعمليات يومية في النظام المحاسبي
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={syncAllInstallments}
                  disabled={installmentLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {installmentLoading ? 'جاري التزامن...' : '🔄 تزامن جميع الدفعات'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Installment Plans with Operations */}
          <Card>
            <CardHeader>
              <CardTitle>📅 خطط التقسيط والعمليات المرتبطة</CardTitle>
              <CardDescription>
                عرض خطط التقسيط مع العمليات اليومية المُسجلة منها
              </CardDescription>
            </CardHeader>
            <CardContent>
              {installmentLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {installmentPlans.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">لا توجد خطط تقسيط في هذه الوكالة</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {installmentPlans.map((plan) => (
                        <Card key={plan.id} className="border border-brand-200">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg text-brand-800">
                              خطة التقسيط #{plan.id?.substring(0, 8)}
                            </CardTitle>
                            <CardDescription className="text-sm">
                              المبلغ الإجمالي: {plan.total_amount?.toLocaleString()} دج
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">عدد الأقساط:</span>
                                <div className="font-bold text-brand-600">{plan.number_of_installments}</div>
                              </div>
                              <div>
                                <span className="text-gray-600">الحالة:</span>
                                <div className={`font-bold ${
                                  plan.status === 'active' ? 'text-green-600' :
                                  plan.status === 'completed' ? 'text-brand-600' : 'text-red-600'
                                }`}>
                                  {plan.status === 'active' ? '🟢 نشط' :
                                   plan.status === 'completed' ? '✅ مكتمل' : '❌ ملغي'}
                                </div>
                              </div>
                              <div>
                                <span className="text-gray-600">دفعات مدفوعة:</span>
                                <div className="font-bold text-green-600">
                                  {plan.totals?.paid_payments || 0} / {plan.totals?.total_payments || 0}
                                </div>
                              </div>
                              <div>
                                <span className="text-gray-600">المبلغ المدفوع:</span>
                                <div className="font-bold text-green-600">
                                  {plan.totals?.total_paid_amount?.toLocaleString() || 0} دج
                                </div>
                              </div>
                            </div>
                            
                            <div className="text-center">
                              <Button
                                size="sm"
                                onClick={() => {
                                  // Navigate to enhanced installments for details
                                  alert('للتفاصيل الكاملة اذهب إلى نظام التقسيط المتكامل');
                                }}
                                className="bg-brand-600 hover:bg-brand-700 text-white text-xs"
                              >
                                📋 عرض التفاصيل
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <Card>
          <CardHeader>
            <CardTitle>📊 التقرير المالي اليومي</CardTitle>
            <CardDescription>
              تقرير مفصل لجميع الحركات المالية في يوم محدد
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <Label>التاريخ:</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-48"
              />
              <Button onClick={fetchDailyReport} className="bg-brand-600 hover:bg-brand-700">
                📊 إنشاء التقرير
              </Button>
              {dailyReport && (
                <Button
                  onClick={async () => {
                    // 🆕 Single-day print: delegates to the ledger PDF
                    // engine with start_date=end_date=selectedDate so the
                    // user can print the day-report from the same screen
                    // without duplicating templates.
                    try {
                      await askPdfOrXlsx(
                        `${API}/reports/financial-ledger/${user.agency_id}/print`,
                        { start_date: selectedDate, end_date: selectedDate, type_filter: 'all' }
                      );
                    } catch (err) {
                      alert('فشل في الطباعة: ' + err.message);
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="daily-report-print-btn"
                >
                  🖨️ طباعة تقرير اليوم
                </Button>
              )}
            </div>

            {dailyReport && (
              <div className="mt-6 space-y-4">
                {/* Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold mb-3">ملخص اليوم ({selectedDate}):</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-lg font-bold text-green-600">
                        {dailyReport.summary?.daily_revenue?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">إيرادات اليوم</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-brand-600">
                        {dailyReport.summary?.daily_transfers?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">تحويلات اليوم</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-red-600">
                        {dailyReport.summary?.daily_expenses?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">مصاريف اليوم</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-purple-600">
                        {dailyReport.summary?.current_balance?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">الرصيد الحالي</div>
                    </div>
                  </div>
                </div>

                {/* Operations */}
                {dailyReport.operations && dailyReport.operations.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-2">العمليات اليومية ({dailyReport.operations.length}):</h4>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-right">رقم العملية</TableHead>
                            <TableHead className="text-right">العميل</TableHead>
                            <TableHead className="text-right">الخدمة</TableHead>
                            <TableHead className="text-right">المبلغ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyReport.operations.map((op) => (
                            <TableRow key={op.id}>
                              <TableCell>{op.operation_no}</TableCell>
                              <TableCell>{op.client_name}</TableCell>
                              <TableCell>{op.service_name}</TableCell>
                              <TableCell className="font-semibold text-green-600">
                                {op.final_price?.toLocaleString()} دج
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 🆕 LEDGER (date-range) — bank-statement style summary the
                manager uses to track every single money movement in any
                period. Always visible underneath the daily report so the
                user can choose: a single day, or "from / to". */}
            <div className="mt-10 pt-6 border-t-2 border-amber-200" data-testid="ledger-section">
              <h3 className="text-lg font-bold text-amber-900 mb-2">
                📒 دفتر الحركات المالية (فترة قابلة للتحديد)
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                كل عملية مالية في الفترة المحدَّدة (إيرادات + مصاريف + تحويلات) برقمها وسعرها، مع الحوصلة الكاملة.
              </p>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <Label>من تاريخ</Label>
                  <Input type="date" value={ledgerStart} onChange={(e) => setLedgerStart(e.target.value)}
                    className="w-44" data-testid="ledger-start-date" />
                </div>
                <div>
                  <Label>إلى تاريخ</Label>
                  <Input type="date" value={ledgerEnd} onChange={(e) => setLedgerEnd(e.target.value)}
                    className="w-44" data-testid="ledger-end-date" />
                </div>
                <div>
                  <Label>نوع الحركة</Label>
                  <select value={ledgerFilter} onChange={(e) => setLedgerFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md w-44" data-testid="ledger-type-filter">
                    <option value="all">📋 الكل</option>
                    <option value="revenue">💚 المداخيل فقط</option>
                    <option value="expense">🔴 المصاريف فقط</option>
                    <option value="transfer">🔵 التحويلات فقط</option>
                  </select>
                </div>
                <Button onClick={fetchLedger}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={ledgerLoading}
                  data-testid="ledger-generate-btn">
                  {ledgerLoading ? '⏳ جاري الإنشاء…' : '📊 إنشاء دفتر الحركات'}
                </Button>
                {ledger && (
                  <Button onClick={handlePrintLedger}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="ledger-print-btn">
                    🖨️ طباعة الدفتر
                  </Button>
                )}
              </div>

              {ledger && (
                <div className="space-y-5">
                  {/* 6-number summary */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-center" data-testid="ledger-summary">
                    {[
                      { label: '💚 المداخيل', val: ledger.summary?.total_revenue, cls: 'text-green-700 bg-green-50 border-green-200' },
                      { label: '🔴 المصاريف', val: ledger.summary?.total_expenses, cls: 'text-red-700 bg-red-50 border-red-200' },
                      { label: '🔵 التحويلات', val: ledger.summary?.total_transfers, cls: 'text-blue-700 bg-blue-50 border-blue-200' },
                      { label: '💰 الرصيد الافتتاحي', val: ledger.summary?.opening_balance, cls: 'text-gray-700 bg-gray-50 border-gray-200' },
                      { label: '🏦 الرصيد الختامي', val: ledger.summary?.closing_balance, cls: 'text-purple-700 bg-purple-50 border-purple-200' },
                      { label: '📊 صافي الفترة', val: ledger.summary?.net_change,
                        cls: (ledger.summary?.net_change || 0) >= 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200' },
                    ].map((c, i) => (
                      <div key={i} className={`border-2 rounded-lg p-2 ${c.cls}`}>
                        <div className="text-xs font-semibold">{c.label}</div>
                        <div className="text-base font-extrabold mt-1">
                          {Number(c.val || 0).toLocaleString()} دج
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* By-service breakdown */}
                  {ledger.by_service?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ledger-by-service">
                      <div className="font-bold text-amber-900 mb-2">🧮 حوصلة حسب نوع الخدمة (المداخيل):</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {ledger.by_service.map((s, i) => (
                          <div key={i} className="bg-white rounded p-2 border border-amber-200">
                            <div className="text-xs text-gray-600">{s.label}</div>
                            <div className="text-sm font-bold text-amber-800">
                              {Number(s.total || 0).toLocaleString()} دج
                              <span className="text-xs text-gray-500 mr-1">({s.count})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ledger lines table */}
                  <div className="overflow-x-auto">
                    <div className="text-sm text-gray-600 mb-1">
                      عدد الحركات: <b>{ledger.lines?.length || 0}</b> سطر
                      {ledgerFilter !== 'all' && <span className="text-amber-700"> · فلتر مفعَّل</span>}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-right">النوع</TableHead>
                          <TableHead className="text-right">رقم العملية</TableHead>
                          <TableHead className="text-right">التاريخ والوقت</TableHead>
                          <TableHead className="text-right">الخدمة</TableHead>
                          <TableHead className="text-right">الوصف</TableHead>
                          <TableHead className="text-right">الموظف</TableHead>
                          <TableHead className="text-right">المبلغ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(ledger.lines || [])
                          .filter(l => ledgerFilter === 'all' || l.type === ledgerFilter)
                          .map((l, i) => (
                          <TableRow key={i} data-testid={`ledger-line-${i}`}>
                            <TableCell className="text-sm">{l.type_label}</TableCell>
                            <TableCell className="text-sm font-mono" dir="ltr">{l.ref}</TableCell>
                            <TableCell className="text-xs text-gray-700" dir="ltr">{l.datetime}</TableCell>
                            <TableCell className="text-sm">{l.service_name}</TableCell>
                            <TableCell className="text-sm">{l.description}</TableCell>
                            <TableCell className="text-sm">{l.employee}</TableCell>
                            <TableCell className={`text-sm font-bold ${
                              l.sign === '+' ? 'text-green-700' : (l.sign === '-' ? 'text-red-700' : 'text-gray-500')
                            }`}>
                              {l.sign === '+' ? '+' : (l.sign === '-' ? '-' : '·')} {Number(l.amount || 0).toLocaleString()} دج
                            </TableCell>
                          </TableRow>
                        ))}
                        {(ledger.lines?.length || 0) === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-gray-500 py-6">
                              لا توجد حركات مالية في هذه الفترة
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              📷 {(user?.role === 'general_accountant' || user?.role === 'super_admin') ? 
                    'مراجعة وثيقة' : 'إدارة وثيقة'} {imageDialogType === 'transfer' ? 'التحويل' : 'المصروف'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedItemForImage && (
              <div className="text-center">
                <h3 className="font-medium text-gray-900 mb-2">
                  {imageDialogType === 'transfer' 
                    ? `تحويل: ${selectedItemForImage.amount?.toLocaleString()} دج`
                    : `مصروف: ${selectedItemForImage.description}`
                  }
                </h3>
                
                {/* Status indicator for managers/accountants */}
                {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                  <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <span className="text-brand-800 font-medium">🔍 وضع المراجعة</span>
                    </div>
                    <p className="text-sm text-brand-700">
                      {selectedItemForImage.document_image_url ? 
                        '• يمكنك مراجعة الوثيقة المرفوعة أدناه' : 
                        '• لم يتم رفع وثيقة لهذه العملية بعد'
                      }
                    </p>
                    <p className="text-sm text-brand-700">
                      • يمكنك إضافة وثيقة إضافية أو استبدال الوثيقة الحالية
                    </p>
                    <p className="text-sm text-brand-700">
                      • انقر على الصورة أدناه لعرضها بحجم كامل
                    </p>
                  </div>
                )}
                
                <p className="text-sm text-gray-600 mb-4">
                  {selectedItemForImage.document_image_url ? 
                    (user?.role === 'general_accountant' || user?.role === 'super_admin' ? 
                      'مراجعة أو تحديث وثيقة العملية' : 'تغيير صورة الوثيقة'
                    ) : 'إضافة صورة الوثيقة'
                  }
                </p>
              </div>
            )}

            {/* Current Image Preview with Enhanced Info */}
            {selectedItemForImage?.document_image_url && (
              <div className="text-center">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">الوثيقة الحالية:</p>
                  {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      📋 للمراجعة
                    </span>
                  )}
                </div>
                <div className="relative inline-block">
                  <img 
                    src={`${API}${selectedItemForImage.document_image_url}`}
                    alt="الوثيقة الحالية"
                    className="w-32 h-32 object-cover rounded-lg border mx-auto cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => window.open(`${API}${selectedItemForImage.document_image_url}`, '_blank')}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-50 rounded-lg cursor-pointer"
                       onClick={() => window.open(`${API}${selectedItemForImage.document_image_url}`, '_blank')}>
                    <span className="text-white text-sm font-medium">🔍 عرض كامل</span>
                  </div>
                </div>
              </div>
            )}

            {/* File Input */}
            <div>
              <Label htmlFor="financialImageFile">
                {selectedItemForImage?.document_image_url ? 
                  'اختر وثيقة جديدة (سيتم استبدال الوثيقة الحالية):' : 
                  'اختر صورة الوثيقة:'
                }
              </Label>
              <Input
                id="financialImageFile"
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleImageFileChange}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                يدعم: JPG, PNG | الحد الأقصى: 2 ميجابايت
              </p>
            </div>

            {/* Selected Image Preview */}
            {selectedImage && (
              <div className="text-center">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">الوثيقة الجديدة:</p>
                  <span className="text-xs bg-brand-100 text-brand-800 px-2 py-1 rounded-full">
                    📷 جاهزة للرفع
                  </span>
                </div>
                <img 
                  src={URL.createObjectURL(selectedImage)}
                  alt="معاينة الوثيقة الجديدة"
                  className="w-32 h-32 object-cover rounded-lg border mx-auto"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {selectedImage.name} ({Math.round(selectedImage.size / 1024)} KB)
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowImageDialog(false)}
                disabled={uploadingImage}
              >
                إلغاء
              </Button>
              <Button 
                onClick={handleImageUpload}
                disabled={!selectedImage || uploadingImage}
                className="bg-brand-600 hover:bg-brand-700"
              >
                {uploadingImage ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    جاري الرفع...
                  </div>
                ) : (
                  selectedItemForImage?.document_image_url ? 
                    '📷 تحديث الوثيقة' : '📷 رفع الوثيقة'
                )}
              </Button>
            </div>

            {/* Instructions - Enhanced for Managers/Accountants */}
            <div className="bg-brand-50 p-3 rounded-lg">
              <h4 className="font-medium text-brand-900 mb-2">📋 تعليمات:</h4>
              <ul className="text-sm text-brand-800 space-y-1">
                {(user?.role === 'general_accountant' || user?.role === 'super_admin') ? (
                  <>
                    <li>• راجع الوثيقة المرفوعة للتأكد من صحتها ووضوحها</li>
                    <li>• يمكنك إضافة وثيقة إضافية أو استبدال الوثيقة الحالية</li>
                    <li>• تأكد من أن الوثيقة تطابق تفاصيل العملية المالية</li>
                    <li>• الوثيقة ستكون دليلاً للمراجعة والتدقيق</li>
                  </>
                ) : (
                  <>
                    <li>• قم برفع صورة للوثيقة المرتبطة بهذه العملية المالية</li>
                    <li>• مثال: وصل، سند، فاتورة، إيصال، إلخ</li>
                    <li>• الصورة ستكون دليلاً على صحة العملية المالية</li>
                    <li>• سيتم ضغط الصورة تلقائياً لتوفير المساحة</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Guided "Upload Signed Receipt + Confirm" modal === */}
      <Dialog
        open={!!confirmFlowTransfer}
        onOpenChange={(o) => { if (!o) { setConfirmFlowTransfer(null); setConfirmFlowFile(null); } }}
      >
        <DialogContent className="sm:max-w-[560px]" dir="rtl" data-testid="signed-receipt-modal">
          <DialogHeader>
            <DialogTitle className="text-xl">
              📷 تصوير الوصل المُوقَّع لتأكيد التحويل
            </DialogTitle>
          </DialogHeader>
          {confirmFlowTransfer && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <div className="flex flex-wrap gap-3 justify-between">
                  <div>
                    <span className="text-gray-500">🔖 الرقم المرجعي: </span>
                    <span className="font-mono font-bold">{confirmFlowTransfer.reference_no || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">💰 المبلغ: </span>
                    <span className="font-bold">{confirmFlowTransfer.amount?.toLocaleString()} دج</span>
                  </div>
                </div>
                {confirmFlowTransfer.recipient_name && (
                  <div className="mt-2">
                    <span className="text-gray-500">👤 المُستَلِم: </span>
                    <span className="font-bold">{confirmFlowTransfer.recipient_name}</span>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs leading-relaxed text-blue-900">
                💡 ارفع صورة الوصل الورقي بعد توقيع المُستَلِم. هذا يضمن أرشيف رقمي
                كامل مرتبط بالرقم المرجعي — لا تحتاج لحفظ أي ورق خارج التطبيق.
              </div>

              <div>
                <Label>📷 صورة الوصل المُوقَّع</Label>
                <Input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => setConfirmFlowFile(e.target.files?.[0] || null)}
                  data-testid="signed-receipt-file-input"
                />
                {confirmFlowFile && (
                  <p className="text-xs text-green-700 mt-1">
                    ✅ تم اختيار: {confirmFlowFile.name} ({(confirmFlowFile.size / 1024).toFixed(0)} KB)
                  </p>
                )}
                <p className="text-[11px] text-gray-500 mt-1">
                  💡 على الهاتف: سيُفتح الكاميرا مباشرة. على الكمبيوتر: اختر صورة من جهازك.
                </p>
              </div>

              <div className="flex justify-between items-center gap-2 pt-3 border-t flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => { setConfirmFlowTransfer(null); setConfirmFlowFile(null); }}
                  data-testid="signed-receipt-cancel"
                >
                  ❌ إلغاء
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm(
                        '⚠️ هل أنت متأكد من التأكيد بدون صورة الوصل؟\n' +
                        'يُفضّل دائماً إرفاق صورة الوصل الموقّع للأرشيف الرقمي.'
                      )) return;
                      const tid = confirmFlowTransfer.id;
                      setConfirmFlowTransfer(null);
                      setConfirmFlowFile(null);
                      await _doConfirmTransfer(tid);
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    disabled={confirmFlowBusy}
                    data-testid="signed-receipt-skip"
                  >
                    ⏭️ تجاوز وتأكيد
                  </Button>
                  <Button
                    onClick={submitSignedReceiptAndConfirm}
                    disabled={confirmFlowBusy || !confirmFlowFile}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold"
                    data-testid="signed-receipt-submit"
                  >
                    {confirmFlowBusy ? '⏳ جاري الرفع...' : '📤 رفع وتأكيد التحويل'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW — Traceability dialogs (additive, do not alter existing flow) */}
      <BalanceDetailsDialog
        open={balanceDetailsOpen}
        onClose={() => setBalanceDetailsOpen(false)}
        agencyId={user?.agency_id}
        initialRecipient={transferRecipient}
        initialNotes={transferNotes}
        onTransferred={() => {
          setTransferAmount('');
          setTransferNotes('');
          setTransferRecipient('');
          setTimeout(() => { fetchFinancialData(); }, 400);
        }}
      />
      <TransferEntriesDialog
        open={transferEntriesOpen}
        transferId={transferEntriesId}
        onClose={() => { setTransferEntriesOpen(false); setTransferEntriesId(null); }}
      />

      {/* 🆕 Comprehensive cash inflows audit — every source, every period */}
      <CashInflowsAuditDialog
        open={cashInflowsOpen}
        onClose={() => setCashInflowsOpen(false)}
        agencyId={user?.agency_id}
        agencyName={user?.agency_name}
      />

      {/* 🆕 In-page reject confirmation modal (replaces browser confirm()
          which was being auto-dismissed in embedded preview iframes). */}
      {rejectModal.open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="reject-transfer-modal"
          onClick={() => !rejectModal.busy && setRejectModal({ open: false, transferId: null, busy: false })}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border-2 border-red-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 rounded-full p-3 text-2xl">⚠️</div>
              <div>
                <h3 className="text-lg font-bold text-red-900">تأكيد رفض التحويل</h3>
                <p className="text-sm text-gray-600">هل أنت متأكد من رفض هذا التحويل؟</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 mb-4">
              💡 المبلغ سيرجع تلقائياً إلى الرصيد الحالي للوكالة، والوصولات المرتبطة به ستعود للقائمة القابلة للتحويل.
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                disabled={rejectModal.busy}
                onClick={() => setRejectModal({ open: false, transferId: null, busy: false })}
                data-testid="reject-transfer-cancel-btn"
              >
                إلغاء
              </Button>
              <Button
                disabled={rejectModal.busy}
                onClick={confirmRejectTransfer}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="reject-transfer-confirm-btn"
              >
                {rejectModal.busy ? '⏳ جاري الرفض...' : '❌ نعم، ارفض التحويل'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Cash Transfer v2 dialog — mounts once at the root of the page */}
      <NewCashTransferDialog
        open={ctV2DialogOpen}
        onOpenChange={setCtV2DialogOpen}
        agencyId={user?.agency_id}
        onSuccess={() => { fetchFinancialData && fetchFinancialData(); }}
      />
    </div>
  );
});


export default FinancialManagement;
